/*
** $Id$
**
** Copyright 2018 Port25 Solutions, Inc.
** All rights reserved.
*/


import axios from 'axios';
import { PmtaError } from './PmtaError'


/**
 * A client for talking to PowerMTA over HTTP.
 */
export default class HttpsClient {
    constructor(host, port) {
        this.pmtaUrl = host ? 'https://' + host + ':' + port : ''
    }


    /**
     * Makes URL-parameters for the given options.
     *
     * @param options
     *      options for which URL-parameters are desired.
     * @return
     *      URL-parameters for the given options.
     */
    makeUrlParams(options) {
        let defaultParams = {
            format: 'json'
        }
        return {
            params: Object.assign(defaultParams, options)
        }
    }


    /**
     * Reads the given path with HTTP GET.
     *
     * @param path
     *      path relative to the web root. e.g., /status for the status page.
     * @param urlParams
     *      JSON object containing name/value pairs of any URL parameters to
     *      be supplied as part of the HTTP GET request.
     * @return
     *      response from PowerMTA.
     */
    async get(path, urlParams) {
        this.checkPath(path)
        let url = this.baseUrl() + path
        let params = this.makeUrlParams(urlParams)
        return await this.checkResponse(() => {
            return axios.get(url, params)
        })
    }


    async checkResponse(getter) {
        let response
        try {
            response = await getter()
        }
        catch (ex) {
            // See "Handling errors" at https://github.com/axios/axios
            if (ex.response) {
                throw new PmtaError(ex.response.data.message)
            }
            throw ex
        }
        if (response && response.data.status === 'fail') {
            throw new PmtaError(response.data.message, response.data.CSRFToken)
        }
        return response
    }


    checkPath(path) {
        if (path.indexOf('?') > -1) {
            throw new PmtaError(
                "URL parameters must be supplied as a separate JSON object")
        }
    }


    /**
     * Sends MIME-structured form data to PowerMTA via HTTP POST.
     *
     * @param path
     *      path relative to the web root. e.g., /enterLak for the LAK page.
     * @param parts
     *      JSON object containing name/value pairs of MIME parts to send.
     * @return
     *      response from PowerMTA.
     */
    async post(path, parts) {
        this.checkPath(path)
        let url = this.baseUrl() + path
        const formData = new FormData();
        Object.keys(parts).forEach((name) => {
            formData.append(name, parts[name])
        })
        const config = {
            headers: {
                'content-type': 'multipart/form-data'
            }
        }
        return await this.checkResponse(() => {
            return axios.post(url, formData, config)
        })
    }


    /**
     * Returns base URL for reaching PowerMTA.
     */
    baseUrl() {
        return this.pmtaUrl
    }
}


export let pmta = new HttpsClient()
